import { useState, useEffect } from 'react';
import { X, Upload, Crop as CropIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { ImageCropModal } from './ImageCropModal';
import { toast } from 'sonner';
import { CATEGORIES } from '../constants/categories';
import { parseTimeParts } from '../services/eventService';
import { getCroppedImageBlob } from '../lib/imageCrop';

const CITIES = [
  'Joinville'
];

export const EventForm = ({ onClose, onSubmit, initialData = null }) => {
  // Helper to convert Firestore Timestamp to YYYY-MM-DD string without timezone shift
  const formatDateForInput = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    // Use local date components to avoid timezone shift
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to extract "HH:MM" from a Firestore Timestamp that already has the
  // hour embedded (new-format events, same pattern as the Flutter app).
  const formatTimeForInput = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (date.getHours() === 0 && date.getMinutes() === 0) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    city: initialData?.city || 'Joinville',
    location: initialData?.location || '',
    date: formatDateForInput(initialData?.date),
    startDate: formatDateForInput(initialData?.startDate) || formatDateForInput(initialData?.date),
    endDate: formatDateForInput(initialData?.endDate) || formatDateForInput(initialData?.date),
    // Legacy events store the hour in "time"; new-format events have it embedded
    // in startDate/date directly (no "time" field is saved going forward).
    time: initialData?.time || formatTimeForInput(initialData?.startDate) || formatTimeForInput(initialData?.date) || '',
    // Legacy events had endDate fixed at 23:59:59; new-format events have the
    // real end time embedded (no "endTime" field is saved going forward).
    endTime: formatTimeForInput(initialData?.endDate) || '',
    isPaid: initialData?.isFree === false || false,  // Convert isFree back to isPaid for UI
    ticketPrice: initialData?.ticketPrice || 0,
    ticketUrl: initialData?.ticketUrl || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialData?.imageUrl || null);
  const [loading, setLoading] = useState(false);

  // Estado do overlay de recorte 16:9 — cropImageSrc é sempre um blob: URL
  // local (do arquivo recém-escolhido OU da imagem atual buscada via fetch),
  // nunca a URL remota do Storage diretamente.
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [fetchingExistingImage, setFetchingExistingImage] = useState(false);

  // Revoga o blob URL quando o preview muda ou o componente desmonta.
  // Não faz nada para URLs do Firebase Storage (não começam com "blob:").
  useEffect(() => {
    if (!imagePreview?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  // Imagem exibida hoje ainda é exatamente a que já estava salva no evento
  // (nenhum arquivo novo escolhido, nenhum recorte aplicado nesta sessão) —
  // é esse o caso em que faz sentido oferecer "Ajustar recorte" na imagem
  // já publicada, em vez de exigir escolher um arquivo novo do disco.
  const isShowingOriginalImage = Boolean(initialData?.imageUrl) && imagePreview === initialData.imageUrl;

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // permite escolher o mesmo arquivo de novo caso o usuário cancele o recorte
    setCropImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
  };

  // Busca a imagem já publicada (URL do Storage) pra reabrir no recorte, sem
  // exigir que o usuário escolha um arquivo novo do disco.
  const handleAdjustExistingCrop = async () => {
    if (!initialData?.imageUrl) return;
    setFetchingExistingImage(true);
    try {
      const response = await fetch(initialData.imageUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      setCropImageSrc(URL.createObjectURL(blob));
      setCropModalOpen(true);
    } catch (error) {
      console.error('Erro ao carregar imagem atual para recorte:', error);
      toast.error('Não foi possível carregar a imagem atual para ajustar o recorte.');
    } finally {
      setFetchingExistingImage(false);
    }
  };

  const handleCropCancel = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
    setCropModalOpen(false);
  };

  // Gera o blob final (recorte 16:9 já aplicado nos pixels) e o coloca no
  // mesmo lugar que um arquivo escolhido manualmente ocuparia — dali em
  // diante segue o fluxo existente (imageFile → onSubmit → uploadEventImage),
  // sem nenhuma mudança de contrato.
  const handleCropConfirm = async (croppedAreaPixels) => {
    try {
      const croppedBlob = await getCroppedImageBlob(cropImageSrc, croppedAreaPixels);
      setImageFile(croppedBlob);
      setImagePreview(URL.createObjectURL(croppedBlob));
    } catch (error) {
      console.error('Erro ao gerar imagem recortada:', error);
      toast.error('Não foi possível aplicar o recorte. Tente novamente.');
    } finally {
      if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
      setCropImageSrc(null);
      setCropModalOpen(false);
    }
  };

  // Combina uma string de data ("YYYY-MM-DD") com uma de horário ("HH:MM") em
  // um Date local, sem deixar o motor JS interpretar a string (mesma
  // estratégia segura usada em eventService.js).
  const buildDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    const { hours, minutes } = parseTimeParts(timeStr);
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.category || !formData.city || !formData.location || !formData.date || !formData.time || !formData.endTime) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    const startDateTime = buildDateTime(formData.startDate || formData.date, formData.time);
    const endDateTime = buildDateTime(formData.endDate || formData.date, formData.endTime);

    if (startDateTime && endDateTime && endDateTime <= startDateTime) {
      toast.error('A hora de encerramento deve ser posterior à hora de início do evento.');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData, imageFile);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erro ao salvar evento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {initialData ? 'Editar Evento' : 'Novo Evento'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-form-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Nome do evento"
              data-testid="event-title-input"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o evento"
              rows={4}
              data-testid="event-description-input"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="category">Categoria *</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="event-category-select"
                required
              >
                <option value="">Selecione uma categoria</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">Cidade *</Label>
              <select
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="event-city-select"
                required
              >
                {CITIES.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Localização (Endereço completo) *</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Rua, número, bairro"
              data-testid="event-location-input"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

  <div className="space-y-4">

  {/* LINHA 1 → DATAS */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

    {/* DATA INÍCIO */}
    <div className="space-y-2">
      <Label htmlFor="date">Data *</Label>
      <Input
        id="date"
        type="date"
        value={formData.date}
        onChange={(e) => {
          setFormData({
            ...formData,
            date: e.target.value,
            startDate: e.target.value,
          });
        }}
        required
      />
    </div>

    {/* DATA FINAL */}
    <div className="space-y-2">
      <Label>Data final</Label>
      <Input
        type="date"
        value={formData.endDate}
        onChange={(e) =>
          setFormData({ ...formData, endDate: e.target.value })
        }
      />
    </div>

  </div>

  {/* LINHA 2 → HORÁRIOS */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

    {/* HORÁRIO INÍCIO */}
    <div className="space-y-2">
      <Label htmlFor="time">Horário Início *</Label>
      <Input
        id="time"
        type="time"
        value={formData.time}
        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
        required
      />
    </div>

    {/* HORÁRIO FINAL */}
    <div className="space-y-2">
      <Label htmlFor="endTime">Horário Final *</Label>
      <Input
        id="endTime"
        type="time"
        value={formData.endTime}
        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
        required
      />
    </div>

  </div>

</div>
</div> 

          <div className="space-y-4 p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="isPaid">Evento Pago</Label>
                <p className="text-sm text-muted-foreground">Este evento requer pagamento?</p>
              </div>
              <Switch
                id="isPaid"
                checked={formData.isPaid}
                onCheckedChange={(checked) => {
                  setFormData({ 
                    ...formData, 
                    isPaid: checked,
                    ticketPrice: checked ? formData.ticketPrice : 0
                  });
                }}
                data-testid="event-ispaid-switch"
              />
            </div>

            {formData.isPaid && (
              <div className="space-y-4 pt-2 border-t border-border">
                <div className="space-y-2">
                  <Label htmlFor="ticketPrice">Preço do Ingresso (R$)</Label>
                  <Input
                    id="ticketPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.ticketPrice}
                    onChange={(e) => setFormData({ ...formData, ticketPrice: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    data-testid="event-ticketprice-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ticketUrl">Link para Compra de Ingressos</Label>
                  <Input
                    id="ticketUrl"
                    type="url"
                    value={formData.ticketUrl}
                    onChange={(e) => setFormData({ ...formData, ticketUrl: e.target.value })}
                    placeholder="https://..."
                    data-testid="event-ticketurl-input"
                  />
                </div>
              </div>
            )}

          </div>

          <div className="space-y-2">
            <Label>Imagem do Evento</Label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
              {imagePreview ? (
                <div className="space-y-2">
                  <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                  <div className="flex flex-wrap justify-center gap-2">
                    {isShowingOriginalImage && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={handleAdjustExistingCrop}
                        disabled={fetchingExistingImage}
                        data-testid="adjust-crop-btn"
                      >
                        <CropIcon className="w-3.5 h-3.5" />
                        {fetchingExistingImage ? 'Carregando...' : 'Ajustar recorte'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                    >
                      Remover imagem
                    </Button>
                  </div>
                </div>
              ) : (
                <label htmlFor="image" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Clique para fazer upload</p>
                  <input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    data-testid="event-image-input"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="flex-1"
              disabled={loading}
              data-testid="submit-event-btn"
            >
              {loading ? 'Salvando...' : initialData ? 'Atualizar Evento' : 'Criar Evento'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>

    {cropModalOpen && (
      <ImageCropModal
        imageSrc={cropImageSrc}
        aspect={16 / 9}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
    )}
    </>
  );
};

